import React from "react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";
import { Button } from "./button";
import {
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
} from "lucide-react";

interface PaginationControlsProps {
	pageIndex: number;
	pageSize: number;
	totalItems: number;
	pageCount: number;
	canPreviousPage: boolean;
	canNextPage: boolean;
	onPageSizeChange: (pageSize: number) => void;
	onPageIndexChange: (pageIndex: number) => void;
	onPreviousPage: () => void;
	onNextPage: () => void;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
	pageIndex,
	pageSize,
	totalItems,
	pageCount,
	canPreviousPage,
	canNextPage,
	onPageSizeChange,
	onPageIndexChange,
	onPreviousPage,
	onNextPage,
}) => {
	const startItem = pageIndex * pageSize + 1;
	const endItem = Math.min((pageIndex + 1) * pageSize, totalItems);

	if (totalItems === 0) {
		return null;
	}

	return (
		<div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t bg-white">
			{/* Left side - Page info and size selector */}
			<div className="flex items-center gap-4 flex-wrap">
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-700 hidden sm:inline">
						Zeilen pro Seite:
					</span>
					<span className="text-sm text-gray-700 sm:hidden">
						Zeilen:
					</span>
					<Select
						value={pageSize.toString()}
						onValueChange={(value) => {
							onPageSizeChange(Number(value));
							onPageIndexChange(0); // Reset to first page when changing page size
						}}
					>
						<SelectTrigger className="h-8 w-[70px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="25">25</SelectItem>
							<SelectItem value="50">50</SelectItem>
							<SelectItem value="100">100</SelectItem>
							<SelectItem value="200">200</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="text-sm text-gray-700 text-center sm:text-left">
					<span className="hidden sm:inline">
						{totalItems > 0
							? `Zeige ${startItem}-${endItem} von ${totalItems} Einträgen`
							: "Keine Einträge"}
					</span>
					<span className="sm:hidden">
						{totalItems > 0
							? `${startItem}-${endItem} / ${totalItems}`
							: "0 / 0"}
					</span>
				</div>
			</div>

			{/* Right side - Navigation controls */}
			<div className="flex items-center gap-2">
				{/* First page button */}
				<Button
					variant="outline"
					size="icon"
					className="h-8 w-8"
					onClick={() => onPageIndexChange(0)}
					disabled={!canPreviousPage}
					aria-label="Erste Seite"
				>
					<ChevronsLeft className="h-4 w-4" />
				</Button>

				{/* Previous page button */}
				<Button
					variant="outline"
					size="icon"
					className="h-8 w-8"
					onClick={onPreviousPage}
					disabled={!canPreviousPage}
					aria-label="Vorherige Seite"
				>
					<ChevronLeft className="h-4 w-4" />
				</Button>

				{/* Page number display */}
				<div className="flex items-center gap-1">
					<span className="text-sm text-gray-700">
						Seite {pageIndex + 1} von {pageCount}
					</span>
				</div>

				{/* Next page button */}
				<Button
					variant="outline"
					size="icon"
					className="h-8 w-8"
					onClick={onNextPage}
					disabled={!canNextPage}
					aria-label="Nächste Seite"
				>
					<ChevronRight className="h-4 w-4" />
				</Button>

				{/* Last page button */}
				<Button
					variant="outline"
					size="icon"
					className="h-8 w-8"
					onClick={() => onPageIndexChange(pageCount - 1)}
					disabled={!canNextPage}
					aria-label="Letzte Seite"
				>
					<ChevronsRight className="h-4 w-4" />
				</Button>
			</div>
		</div>
	);
};
